import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  Database, RefreshCw, Play, CheckCircle2, XCircle, Loader2, Mail, MailX,
  History, AlertTriangle, CloudUpload, ExternalLink, Settings, HelpCircle, Check, Copy, Unplug
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OAUTH_REDIRECT_URI = `${process.env.REACT_APP_BACKEND_URL}/api/backup/drive/oauth/callback`;

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch (e) { return iso; }
};
const fmtKB = (kb) => kb >= 1024 ? `${(kb / 1024).toFixed(2)} MB` : `${kb} KB`;

const BackupHistory = () => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoreInput, setRestoreInput] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [driveTesting, setDriveTesting] = useState(false);

  // Drive configuration dialog state
  const [configOpen, setConfigOpen] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [configMethod, setConfigMethod] = useState('oauth');
  const [folderId, setFolderId] = useState('0AFDLtLhov8CbUk9PVA');
  const [serviceAccountJson, setServiceAccountJson] = useState('');
  const [oauthClientId, setOauthClientId] = useState('');
  const [oauthClientSecret, setOauthClientSecret] = useState('');
  const [showInstructions, setShowInstructions] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const config = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [runsRes, statusRes] = await Promise.all([
        axios.get(`${API}/backup/runs`, config()),
        axios.get(`${API}/backup/status`, config()),
      ]);
      setRuns(runsRes.data?.runs || []);
      const st = statusRes.data || null;
      setStatus(st);
      if (st?.drive?.folder_id) setFolderId(st.drive.folder_id);
      if (st?.drive?.oauth_client_id) setOauthClientId(st.drive.oauth_client_id);
      if (st?.drive?.auth_method) setConfigMethod(st.drive.auth_method === 'service_account' ? 'sa' : 'oauth');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load backup history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API}/backup/trigger`, {}, config());
      const r = res.data?.result || {};
      if (r.status === 'error' || r.error) {
        toast.error(`Backup failed: ${r.error || 'unknown error'}`);
      } else {
        const driveNote = r.drive_uploaded ? ' (Saved to Google Drive)' : ' (Drive upload pending/failed)';
        toast.success(`Backup complete: ${r.total_documents} docs across ${r.collections} collections (${fmtKB(r.zip_size_kb || 0)})${driveNote}`);
      }
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to trigger backup');
    } finally {
      setRunning(false);
    }
  };

  const handleDriveTest = async () => {
    setDriveTesting(true);
    try {
      const res = await axios.post(`${API}/backup/drive/test`, {}, config());
      toast.success(res.data?.message || 'Google Drive connection verified');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Google Drive test failed');
    } finally {
      setDriveTesting(false);
    }
  };

  const handleFileDrop = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      setServiceAccountJson(event.target?.result || '');
      toast.info(`Loaded key file: ${file.name}`);
    };
    reader.readAsText(file);
  };

  const handleSaveSaConfig = async (e) => {
    e.preventDefault();
    if (!serviceAccountJson.trim()) {
      toast.error('Please paste or upload the Service Account JSON key');
      return;
    }
    setSavingConfig(true);
    try {
      const res = await axios.post(`${API}/backup/drive/config`, {
        folder_id: folderId.trim(),
        service_account_json: serviceAccountJson.trim(),
      }, config());
      toast.success(res.data?.message || 'Google Drive connected successfully!');
      setConfigOpen(false);
      setServiceAccountJson('');
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to connect Google Drive');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleSaveOauthConfig = async () => {
    if (!oauthClientId.trim() || !oauthClientSecret.trim()) {
      toast.error('Enter both the OAuth Client ID and Client Secret');
      return;
    }
    setSavingConfig(true);
    try {
      const res = await axios.post(`${API}/backup/drive/oauth/config`, {
        folder_id: folderId.trim(),
        client_id: oauthClientId.trim(),
        client_secret: oauthClientSecret.trim(),
      }, config());
      toast.success(res.data?.message || 'OAuth credentials saved');
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save OAuth credentials');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleConnectGoogle = async () => {
    setConnecting(true);
    try {
      const res = await axios.get(`${API}/backup/drive/oauth/url`, {
        ...config(), params: { redirect_uri: OAUTH_REDIRECT_URI },
      });
      window.open(res.data.authorization_url, '_blank', 'noopener,width=640,height=720');
      toast.info('Approve access in the new tab, then click Refresh here.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start Google sign-in');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      const res = await axios.post(`${API}/backup/drive/disconnect`, {}, config());
      toast.success(res.data?.message || 'Google Drive disconnected');
      setOauthClientId('');
      setOauthClientSecret('');
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Disconnect failed');
    } finally {
      setDisconnecting(false);
    }
  };

  const copyRedirectUri = async () => {
    try {
      await navigator.clipboard.writeText(OAUTH_REDIRECT_URI);
      toast.success('Redirect URI copied');
    } catch {
      toast.info(OAUTH_REDIRECT_URI);
    }
  };

  const handleRestore = async () => {
    if (!restoreTarget) return;
    setRestoring(true);
    try {
      const res = await axios.post(`${API}/backup/restore`, {
        timestamp: restoreTarget.timestamp,
        confirm_timestamp: restoreInput,
      }, config());
      const r = res.data || {};
      toast.success(`Restored ${r.total_documents} docs across ${r.collections} collections`);
      setRestoreTarget(null);
      setRestoreInput('');
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoring(false);
    }
  };

  const drive = status?.drive || {};
  const driveIsConfigured = !!drive.configured;

  return (
    <Card data-testid="backup-history-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-50 text-blue-600">
              <Database className="w-5 h-5" />
            </div>
            <div>
              <CardTitle>Database Backups</CardTitle>
              <CardDescription>
                {status?.schedule || 'Daily at 08:00 AM IST'}{' '}
                · saved to {driveIsConfigured ? (
                  <a
                    href={drive.folder_link}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-blue-600 hover:underline inline-flex items-center gap-0.5"
                    data-testid="backup-drive-folder-link"
                  >
                    Google Drive <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <span className="font-medium text-amber-600" data-testid="backup-drive-not-configured">
                    Google Drive (not connected)
                  </span>
                )}
                {driveIsConfigured && (
                  <Badge variant="outline" className="ml-2 text-xs" data-testid="backup-drive-method-badge">
                    {drive.auth_method === 'oauth' ? 'Google OAuth' : 'Service Account'}
                  </Badge>
                )}
                {driveIsConfigured && drive.client_email && (
                  <span className="text-xs text-gray-500 block sm:inline sm:ml-2">
                    via <code className="bg-gray-100 px-1 py-0.5 rounded text-gray-700">{drive.client_email}</code>
                  </span>
                )}
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfigOpen(true)}
              className="border-slate-300 text-slate-700 hover:bg-slate-50"
              data-testid="backup-configure-drive-button"
            >
              <Settings className="w-4 h-4 mr-1 text-slate-600" />
              {driveIsConfigured ? 'Configure Drive' : 'Connect Drive'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDriveTest}
              disabled={driveTesting || !driveIsConfigured}
              data-testid="backup-drive-test-button"
              title={driveIsConfigured ? 'Verify Google Drive write access' : 'Connect Drive first'}
            >
              {driveTesting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CloudUpload className="w-4 h-4 mr-1" />}
              Test Drive
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchAll}
              disabled={loading || running}
              data-testid="backup-refresh-button"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button
              size="sm"
              onClick={handleRunNow}
              disabled={running}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              data-testid="backup-run-now-button"
            >
              {running ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
              {running ? 'Running…' : 'Run Backup Now'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="backup-runs-table">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 pr-3 font-medium">Ran at</th>
                <th className="py-2 pr-3 font-medium">Status</th>
                <th className="py-2 pr-3 font-medium">Collections</th>
                <th className="py-2 pr-3 font-medium">Documents</th>
                <th className="py-2 pr-3 font-medium">Size</th>
                <th className="py-2 pr-3 font-medium">Email Notice</th>
                <th className="py-2 pr-3 font-medium">Google Drive</th>
                <th className="py-2 pr-3 text-right font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {!loading && runs.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-gray-500" data-testid="backup-runs-empty">
                    No backups recorded yet. Click &quot;Run Backup Now&quot; to capture one.
                  </td>
                </tr>
              )}
              {runs.map((r) => (
                <tr key={r.timestamp + r.ran_at} className="border-b last:border-0 hover:bg-gray-50" data-testid={`backup-row-${r.timestamp}`}>
                  <td className="py-2 pr-3 text-gray-900 font-medium">{fmtDateTime(r.ran_at)}</td>
                  <td className="py-2 pr-3">
                    {r.status === 'success' ? (
                      <Badge variant="outline" className="text-emerald-700 border-emerald-300 bg-emerald-50">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Success
                      </Badge>
                    ) : r.status === 'restored' ? (
                      <Badge variant="outline" className="text-indigo-700 border-indigo-300 bg-indigo-50" title={`Restored from ${r.restored_from} by ${r.restored_by_email || ''}`}>
                        <History className="w-3 h-3 mr-1" /> Restored
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-red-700 border-red-300 bg-red-50" title={r.error || ''}>
                        <XCircle className="w-3 h-3 mr-1" /> Error
                      </Badge>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-gray-700">{r.collections}</td>
                  <td className="py-2 pr-3 text-gray-700">{(r.total_documents || 0).toLocaleString()}</td>
                  <td className="py-2 pr-3 text-gray-700">{fmtKB(r.zip_size_kb || 0)}</td>
                  <td className="py-2 pr-3">
                    {r.email_sent ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700"><Mail className="w-3.5 h-3.5" /> Sent</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-gray-500"><MailX className="w-3.5 h-3.5" /> —</span>
                    )}
                  </td>
                  <td className="py-2 pr-3">
                    {r.drive_uploaded && r.drive_link ? (
                      <a href={r.drive_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline font-medium" data-testid={`backup-drive-link-${r.timestamp}`}>
                        <ExternalLink className="w-3.5 h-3.5" /> View on Drive
                      </a>
                    ) : r.status === 'success' && r.drive_error ? (
                      <span className="inline-flex items-center gap-1 text-amber-600 text-xs" title={r.drive_error} data-testid={`backup-drive-failed-${r.timestamp}`}>
                        <AlertTriangle className="w-3.5 h-3.5" /> Failed
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-right">
                    {r.status === 'success' && r.restorable ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setRestoreTarget(r); setRestoreInput(''); }}
                        className="text-red-700 border-red-200 hover:bg-red-50"
                        data-testid={`backup-restore-button-${r.timestamp}`}
                      >
                        <History className="w-3.5 h-3.5 mr-1" /> Restore
                      </Button>
                    ) : (
                      <span className="text-xs text-gray-400" title={r.status === 'success' ? 'Zip pruned' : ''}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>

      {/* Google Drive Configuration Dialog */}
      <Dialog open={configOpen} onOpenChange={(v) => !savingConfig && setConfigOpen(v)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="backup-drive-config-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CloudUpload className="w-5 h-5 text-blue-600" />
              Configure Google Drive for Backups
            </DialogTitle>
            <DialogDescription>
              Daily backup ZIPs upload to your Google Drive folder and the latest 90 are kept automatically.
            </DialogDescription>
          </DialogHeader>

          {/* Method toggle */}
          <div className="grid grid-cols-2 gap-2 mt-2" data-testid="drive-method-toggle">
            <button
              type="button"
              onClick={() => setConfigMethod('oauth')}
              className={`p-3 rounded-lg border text-left transition-colors ${configMethod === 'oauth' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
              data-testid="drive-method-oauth-tab"
            >
              <p className="text-sm font-semibold text-gray-900">Google Account (OAuth)</p>
              <p className="text-xs text-gray-500 mt-0.5">Recommended — no service account key needed. Sign in once with Google.</p>
            </button>
            <button
              type="button"
              onClick={() => setConfigMethod('sa')}
              className={`p-3 rounded-lg border text-left transition-colors ${configMethod === 'sa' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
              data-testid="drive-method-sa-tab"
            >
              <p className="text-sm font-semibold text-gray-900">Service Account JSON</p>
              <p className="text-xs text-gray-500 mt-0.5">Requires a downloadable JSON key (may be blocked by org policy).</p>
            </button>
          </div>

          <div className="mt-4">
            <label className="text-sm font-medium text-gray-900 block mb-1">
              Google Drive Folder / Shared Drive ID
            </label>
            <Input
              value={folderId}
              onChange={(e) => setFolderId(e.target.value)}
              placeholder="e.g. 0AFDLtLhov8CbUk9PVA"
              data-testid="drive-folder-id-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              From your Drive URL: <code className="bg-gray-100 px-1 py-0.5 rounded">folders/<b>{folderId || '...'}</b></code>
            </p>
          </div>

          {configMethod === 'oauth' ? (
            <div className="space-y-4 mt-4" data-testid="drive-oauth-section">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-900">Google OAuth Client Credentials</label>
                <button
                  type="button"
                  onClick={() => setShowInstructions(!showInstructions)}
                  className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                  data-testid="drive-oauth-instructions-toggle"
                >
                  <HelpCircle className="w-3.5 h-3.5" />
                  {showInstructions ? 'Hide instructions' : 'How do I create these?'}
                </button>
              </div>

              {showInstructions && (
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-900 space-y-1.5" data-testid="drive-oauth-instructions-box">
                  <p className="font-semibold">One-time setup (~3 minutes):</p>
                  <ol className="list-decimal list-inside space-y-1 pl-1">
                    <li>Open <a href="https://console.cloud.google.com" target="_blank" rel="noreferrer" className="underline font-semibold">Google Cloud Console</a> → select or create a project.</li>
                    <li><b>APIs & Services → Library</b> → search <b>Google Drive API</b> → <b>Enable</b>.</li>
                    <li><b>APIs & Services → OAuth consent screen</b> → External → fill app name/email → add scope <code className="bg-blue-100 px-1">.../auth/drive</code> → add your Gmail under <b>Test users</b>.</li>
                    <li><b>APIs & Services → Credentials → Create Credentials → OAuth client ID</b> → type <b>Web application</b>.</li>
                    <li>Under <b>Authorized redirect URIs</b>, add the Redirect URI shown below, then copy the <b>Client ID</b> and <b>Client Secret</b> here.</li>
                    <li>Click <b>Save Credentials</b>, then <b>Connect Google Drive</b> and approve with the Google account that can access the folder.</li>
                  </ol>
                </div>
              )}

              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">Redirect URI — register this exact URL in Google Cloud Console</label>
                <div className="flex gap-2">
                  <Input value={OAUTH_REDIRECT_URI} readOnly className="font-mono text-xs bg-gray-50" data-testid="drive-oauth-redirect-uri" />
                  <Button type="button" variant="outline" size="sm" onClick={copyRedirectUri} data-testid="drive-oauth-copy-redirect">
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Input
                  value={oauthClientId}
                  onChange={(e) => setOauthClientId(e.target.value)}
                  placeholder="Client ID (…apps.googleusercontent.com)"
                  className="font-mono text-xs"
                  autoComplete="off"
                  data-testid="drive-oauth-client-id"
                />
                <Input
                  type="password"
                  value={oauthClientSecret}
                  onChange={(e) => setOauthClientSecret(e.target.value)}
                  placeholder="Client Secret"
                  className="font-mono text-xs"
                  autoComplete="new-password"
                  data-testid="drive-oauth-client-secret"
                />
              </div>

              {drive.oauth_connected && drive.auth_method === 'oauth' && (
                <Alert className="border-emerald-300 bg-emerald-50" >
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <AlertTitle className="text-emerald-800">Connected</AlertTitle>
                  <AlertDescription className="text-emerald-700 text-xs">
                    A Google account is already authorized. Reconnect only if access was revoked.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          ) : (
            <div className="space-y-2 mt-4" data-testid="drive-sa-section">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-900">Service Account JSON Key</label>
                <button
                  type="button"
                  onClick={() => setShowInstructions(!showInstructions)}
                  className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                  data-testid="drive-sa-instructions-toggle"
                >
                  <HelpCircle className="w-3.5 h-3.5" />
                  {showInstructions ? 'Hide instructions' : 'Where do I get this key?'}
                </button>
              </div>

              {showInstructions && (
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-900 space-y-1.5" data-testid="drive-instructions-box">
                  <p className="font-semibold">Service account steps:</p>
                  <ol className="list-decimal list-inside space-y-1 pl-1">
                    <li>Google Cloud Console → enable <b>Google Drive API</b>.</li>
                    <li><b>IAM & Admin → Service Accounts</b> → create one → <b>Keys → Add Key → JSON</b> (downloads a file).</li>
                    <li>Add the service account email (<code className="bg-blue-100 px-1">.iam.gserviceaccount.com</code>) as <b>Content Manager</b> on the Shared Drive.</li>
                    <li>Upload the JSON file below or paste its content.</li>
                  </ol>
                </div>
              )}

              <Input
                type="file"
                accept=".json,application/json"
                onChange={handleFileDrop}
                className="cursor-pointer file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                data-testid="drive-sa-file-input"
              />
              <Textarea
                value={serviceAccountJson}
                onChange={(e) => setServiceAccountJson(e.target.value)}
                placeholder="Or paste the JSON contents here: { &quot;type&quot;: &quot;service_account&quot;, ... }"
                rows={4}
                className="font-mono text-xs"
                data-testid="drive-sa-json-textarea"
              />
            </div>
          )}

          <DialogFooter className="mt-4 gap-2 flex-wrap">
            {driveIsConfigured && (
              <Button
                type="button"
                variant="outline"
                onClick={handleDisconnect}
                disabled={disconnecting || savingConfig}
                className="text-red-600 border-red-200 hover:bg-red-50 mr-auto"
                data-testid="drive-disconnect-button"
              >
                {disconnecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Unplug className="w-4 h-4 mr-2" />}
                Disconnect
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfigOpen(false)}
              disabled={savingConfig}
              data-testid="drive-config-cancel-button"
            >
              Cancel
            </Button>
            {configMethod === 'oauth' ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSaveOauthConfig}
                  disabled={savingConfig || !oauthClientId.trim() || !oauthClientSecret.trim()}
                  data-testid="drive-oauth-save-button"
                >
                  {savingConfig ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  Save Credentials
                </Button>
                <Button
                  type="button"
                  onClick={handleConnectGoogle}
                  disabled={connecting || savingConfig || !(drive.oauth_client_id_set || oauthClientId.trim())}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="drive-oauth-connect-button"
                >
                  {connecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                  Connect Google Drive
                </Button>
              </>
            ) : (
              <Button
                type="button"
                onClick={handleSaveSaConfig}
                disabled={savingConfig || !serviceAccountJson.trim()}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="drive-config-save-button"
              >
                {savingConfig ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                {savingConfig ? 'Verifying & Saving…' : 'Save & Verify Connection'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore Confirmation Dialog */}
      <Dialog open={!!restoreTarget} onOpenChange={(v) => !restoring && !v && setRestoreTarget(null)}>
        <DialogContent data-testid="backup-restore-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Restore database from this backup?
            </DialogTitle>
            <DialogDescription>
              This will <b>replace every collection</b> in the database with the snapshot from{' '}
              <span className="font-mono">{restoreTarget?.timestamp}</span> ({fmtDateTime(restoreTarget?.ran_at)}).
              Any data added since that backup will be permanently lost.
            </DialogDescription>
          </DialogHeader>

          <Alert variant="destructive" className="mt-2">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Destructive — cannot be undone.</AlertTitle>
            <AlertDescription>
              Backup-run history and pending OTPs are preserved. Type the timestamp below to confirm.
            </AlertDescription>
          </Alert>

          <div className="mt-3 space-y-2">
            <label className="text-sm text-gray-700">Re-type to confirm: <span className="font-mono text-gray-900">{restoreTarget?.timestamp}</span></label>
            <Input
              value={restoreInput}
              onChange={(e) => setRestoreInput(e.target.value)}
              placeholder="Type the timestamp exactly"
              className="font-mono"
              autoFocus
              data-testid="backup-restore-confirm-input"
            />
          </div>

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setRestoreTarget(null)}
              disabled={restoring}
              data-testid="backup-restore-cancel-button"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRestore}
              disabled={restoring || restoreInput !== restoreTarget?.timestamp}
              data-testid="backup-restore-confirm-button"
            >
              {restoring ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <History className="w-4 h-4 mr-2" />}
              {restoring ? 'Restoring…' : 'Yes, restore now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default BackupHistory;
